<template>
	<div
		class="product-details-popup product-details-popup--main"
		:class="{ show: productId }"
	>
		<transition name="right-slide" v-on:after-leave="clearCurrentProductId">
			<div
				v-show="productId"
				class="product-details-wrap"
				:class="{ 'is-loading': !productLoaded }"
			>
				<swiper
					v-if="isMobile"
					class="product-details-swiper"
					:options="swiperOption"
					ref="product-details-swiper"
					@slideChange="onSlideChange"
				>
					<swiper-slide
						v-for="product in productsInCategory"
						:key="product.id"
						class="product-details-slide"
					>
						<template>
							<product-details
								v-if="product.id === currentProductId"
								@set-product-error="setProductError"
								@set-has-images="setHasImages"
								@hide="hideProductDetails"
								@product-loaded="productLoaded = true"
							/>
						</template>
					</swiper-slide>
				</swiper>

				<product-details
					v-else-if="currentProductId"
					@set-product-error="setProductError"
					@set-has-images="setHasImages"
					@hide="hideProductDetails"
					@product-loaded="productLoaded = true"
				/>
			</div>
		</transition>
		<transition name="fade">
			<div
				v-if="productId"
				class="overlay"
				:class="{ 'is-loading': !productLoaded }"
				@click="hideProductDetails"
			/>
		</transition>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { Swiper, SwiperSlide } from 'vue-awesome-swiper'
import { isMobile } from 'mobile-device-detect'

import ProductDetails from '../ProductDetails.vue'
// import ProductDetailsNotFound from '../ProductDetailsNotFound'

export default {
	name: 'ProductDetailsPopup',

	components: {
		Swiper,
		SwiperSlide,
		ProductDetails
		// ProductDetailsNotFound
	},

	data() {
		return {
			currentProductId: false,
			hasActiveProductImage: true,
			hasProductDetailsError: false,
			productLoaded: false,
			swiperOption: {
				slidesPerView: 1,
				spaceBetween: 0,
				touchAngle: 60,
				threshold: 0,
				touchReleaseOnEdges: true,
				touchStartPreventDefault: false,
				passiveListeners: true
			}
		}
	},

	async mounted() {
		this.body = document.querySelector('body')

		if (this.productId) {
			this.lockScroll()

			this.currentProductId = this.productId
			this.changeSlide()
		}
	},

	beforeDestroy() {
		this.unlockScroll()
	},

	computed: {
		...mapGetters('productCatalog', [
			'productsOf',
			'categoryByName',
			'categoryNameByProductId'
		]),
		isMobile() {
			return isMobile
		},

		swiper() {
			const swiperComponent = this.$refs['product-details-swiper']

			return swiperComponent && swiperComponent.$swiper
		},

		categoryName() {
			return this.categoryNameByProductId(this.currentProductId)
		},

		trending() {
			return +this.$route.query.trending
		},

		productsInCategory() {
			const name = this.categoryName
			const products = name ? [...this.productsOf(name)] : []

			if (!products.length) return []

			if (this.trending) {
				// TODO check if this logic exist in store getter
				const trending = products
					.filter(p => p.trendingCategoryRating)
					.sort((a, b) => b.trendingCategoryRating - a.trendingCategoryRating)

				const nonTrending = products
					.filter(p => !p.trendingCategoryRating)
					.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))

				return [...trending, ...nonTrending]
			}

			return products.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
		},

		activeSlideIndex() {
			return this.productsInCategory.findIndex(
				product => product.id === this.currentProductId
			)
		},

		productId() {
			return this.$route.query.productId
		}
	},

	watch: {
		productId(id) {
			if (id) {
				this.lockScroll()

				this.currentProductId = this.productId
				this.changeSlide()
			} else {
				this.unlockScroll()
			}
		},

		productsInCategory(newProducts, oldProducts) {
			const oldHasProducts = oldProducts && oldProducts.length
			const newHasProducts = newProducts && newProducts.length

			if (!oldHasProducts && newHasProducts) {
				this.changeSlide()
			}
		}
	},

	methods: {
		...mapActions('ui', ['lockScroll', 'unlockScroll']),
		clearCurrentProductId() {
			this.currentProductId = null
		},

		setProductError(val) {
			this.hasProductDetailsError = val
		},

		setHasImages(val) {
			this.hasActiveProductImage = val
		},

		changeSlide() {
			if (this.isMobile && this.swiper) {
				this.swiper.slideTo(this.activeSlideIndex, 200, false)
			}
		},

		onSlideChange() {
			this.productLoaded = false
			const product = this.productsInCategory[this.swiper.activeIndex]
			if (!product?.id) return
			const productId = product.id

			if (this.productId && productId && this.productId !== productId) {
				this.$router.push({
					...this.$route,
					query: { ...this.$route.query, productId }
				})
			}
		},

		hideProductDetails() {
			const newQuery = JSON.parse(JSON.stringify(this.$route.query))

			delete newQuery.productId

			this.$router
				.replace({
					...this.$route,
					query: newQuery
				})
				.catch(() => {})

			this.productLoaded = false
		}
	}
}
</script>

<style lang="scss" scoped>
@import './src/assets/styles/variables';

.product-details-popup {
	/deep/ .product-details-price {
		font-size: 20px;
	}

	/deep/ .product-details-alt-price {
		font-size: 16px;
		font-weight: 600;
		color: rgba($white, 0.7);

		.alt-price {
			&::after {
				background-color: rgba($white, 0.7);
			}
		}
	}
}
</style>
